import { CoreEnums } from '@/utils/domain/constants';

const {
  ReportFilterBehavior,
  ReportFilterOperator,
  ReportingDatasetFieldType,
  ReportingDatasetKeyKind,
  ReportingQueryDateBucket,
  ReportingQueryMetricOperation,
  ReportingQueryOrderDirection,
  ReportingQueryType,
} = CoreEnums;

export const BUILDER_SECTIONS = Object.freeze([
  { key: 'settings', labelId: 'reports.builder.section.settings', iconKey: 'settings' },
  { key: 'datasets', labelId: 'reports.builder.section.datasets', iconKey: 'datasets' },
  { key: 'queries', labelId: 'reports.builder.section.queries', iconKey: 'queries' },
  { key: 'filters', labelId: 'reports.builder.section.filters', iconKey: 'filters' },
  { key: 'parameters', labelId: 'reports.builder.section.parameters', iconKey: 'parameters' },
]);

/** Parent Report fields — not builder elements. Saved with PUT /report. */
export const PARENT_SIDEBAR_SECTIONS = Object.freeze([
  { key: 'identity', labelId: 'reports.builder.section.identity', iconKey: 'identity' },
]);

export const SIDEBAR_SECTIONS = Object.freeze([
  ...PARENT_SIDEBAR_SECTIONS,
  ...BUILDER_SECTIONS.filter((section) => section.key !== 'settings'),
]);

export const FILTER_BEHAVIORS = Object.values(ReportFilterBehavior);
export const FILTER_OPERATORS = Object.values(ReportFilterOperator);
export const FILTER_CONTEXT_VALUE = '$currentUser';

/** Ace/antd select may emit a string, `{ value, label }` or an event. Keep filter tokens as strings. */
export const unwrapFilterSelectToken = (raw) => {
  if (raw == null || raw === '') return null;
  if (typeof raw === 'string' || typeof raw === 'number') return String(raw);
  if (typeof raw !== 'object') return null;
  if (Object.prototype.hasOwnProperty.call(raw, 'target')) {
    const fromTarget = raw.target?.value;
    if (fromTarget != null && fromTarget !== '' && typeof fromTarget !== 'object') {
      return String(fromTarget);
    }
  }
  const token = raw.value ?? raw.key ?? raw.id;
  if (token == null || token === '' || typeof token === 'object') return null;
  return String(token);
};

export const normalizeFilterBehavior = (raw, fallback = ReportFilterBehavior.Available) => {
  const token = unwrapFilterSelectToken(raw);
  return FILTER_BEHAVIORS.includes(token) ? token : fallback;
};

export const normalizeFilterOperator = (raw, fallback = ReportFilterOperator.Equal) => {
  const token = unwrapFilterSelectToken(raw);
  return FILTER_OPERATORS.includes(token) ? token : fallback;
};

export const PROCESS_RUNTIME_DATASET_KEYS = Object.freeze([
  'process-instances',
  'action-instances',
  'action-user-instances',
  'form-instances',
  'process-instance-approvers',
]);

/** Global dimensions: omit processIds. Contract Phase 5.3 / UX live list. */
export const GLOBAL_DATASET_KEYS = Object.freeze([
  'users',
  'positions',
  'areas',
  'user-groups',
  'user-group-users',
  'processes',
  'process-groups',
]);

export const CATALOG_KIND = ReportingDatasetKeyKind;

export const CATALOG_KEY_PATTERN = Object.freeze({
  ProcessFormData: 'process-form-data:{processId}:{formId}',
  DynamicEntity: 'dynamic-entity:{dynamicEntityId}',
});

export const unwrapIds = (value) => {
  if (value == null || value === '') return [];
  const rows = Array.isArray(value) ? value : [value];
  return rows.map((row) => row?.id ?? row?.value ?? row).filter(Boolean);
};

export const unwrapCatalogList = (res) => {
  if (Array.isArray(res)) return res;
  if (Array.isArray(res?.items)) return res.items;
  if (Array.isArray(res?.data?.items)) return res.data.items;
  if (Array.isArray(res?.data)) return res.data;
  return [];
};

export const unwrapCatalogItem = (res) => {
  if (!res || typeof res !== 'object') return null;
  if (res.key || res.keyPattern || Array.isArray(res.fields)) return res;
  const nested = res.data;
  if (nested && typeof nested === 'object') return nested;
  return null;
};

export const catalogRefOf = (item) => item?.key || item?.keyPattern || '';

export const isProcessFormDataKey = (value) =>
  String(value || '').startsWith('process-form-data:');

export const isDynamicEntityKey = (value) => String(value || '').startsWith('dynamic-entity:');

export const isProcessFormDataTemplate = (value) =>
  value === CATALOG_KEY_PATTERN.ProcessFormData || isProcessFormDataKey(value);

export const isDynamicEntityTemplate = (value) =>
  value === CATALOG_KEY_PATTERN.DynamicEntity || isDynamicEntityKey(value);

export const findCatalogItem = (catalog = [], refOrKey) => {
  if (!refOrKey) return null;
  const exact = catalog.find((item) => item.key === refOrKey || item.keyPattern === refOrKey);
  if (exact) return exact;
  if (isProcessFormDataKey(refOrKey)) {
    return catalog.find(
      (item) =>
        item.kind === CATALOG_KIND.ProcessFormData ||
        item.keyPattern === CATALOG_KEY_PATTERN.ProcessFormData,
    );
  }
  if (isDynamicEntityKey(refOrKey)) {
    return catalog.find(
      (item) =>
        item.kind === CATALOG_KIND.DynamicEntity ||
        item.keyPattern === CATALOG_KEY_PATTERN.DynamicEntity,
    );
  }
  return null;
};

export const parseDatasetKey = (key) => {
  const raw = String(key || '').trim();
  const formMatch = raw.match(/^process-form-data:([^:]+):([^:]+)$/);
  if (formMatch) {
    return {
      catalogRef: CATALOG_KEY_PATTERN.ProcessFormData,
      kind: CATALOG_KIND.ProcessFormData,
      templateProcessId: formMatch[1],
      templateFormId: formMatch[2],
      key: raw,
    };
  }
  const entityMatch = raw.match(/^dynamic-entity:(.+)$/);
  if (entityMatch) {
    return {
      catalogRef: CATALOG_KEY_PATTERN.DynamicEntity,
      kind: CATALOG_KIND.DynamicEntity,
      templateEntityId: entityMatch[1],
      key: raw,
    };
  }
  return {
    catalogRef: raw,
    kind: CATALOG_KIND.Static,
    key: raw,
  };
};

export const toLabelInValue = (id) => {
  if (id == null || id === '') return undefined;
  if (typeof id === 'object') return id;
  return { id, name: id, value: id, label: id };
};

export const toEntitySelectorValue = (id, name) => {
  const raw = id == null || id === '' ? null : id;
  if (raw == null) return [];
  if (Array.isArray(raw)) return raw.filter(Boolean);
  if (typeof raw === 'object') {
    const nextId = raw.id ?? raw.value ?? raw;
    const nextName = name || raw.name || raw.displayName;
    return [{ ...raw, id: nextId, ...(nextName && nextName !== nextId ? { name: nextName } : {}) }];
  }
  return name && name !== raw ? [{ id: raw, name }] : [{ id: raw }];
};

export const toCatalogSelectorItem = (item) => {
  const id = catalogRefOf(item);
  if (!id) return null;
  return {
    id,
    name: item.name || id,
    kind: item.kind,
    key: item.key,
    keyPattern: item.keyPattern,
    requiresProcessIds: item.requiresProcessIds,
    isGlobalScope: item.isGlobalScope,
  };
};

export const catalogRefFromValue = (value) => unwrapIds(value)[0] || null;

export const catalogRefsFromValue = (value) => unwrapIds(value);

export const buildCatalogDatasetKeyForRef = (ref, values = {}) => {
  if (ref === CATALOG_KEY_PATTERN.ProcessFormData) {
    const processId = unwrapIds(values.templateProcessId)[0];
    const formId = unwrapIds(values.templateFormId)[0];
    if (!processId || !formId) return null;
    return `process-form-data:${processId}:${formId}`;
  }
  if (ref === CATALOG_KEY_PATTERN.DynamicEntity) {
    const entityId = unwrapIds(values.templateEntityId)[0];
    if (!entityId) return null;
    return `dynamic-entity:${entityId}`;
  }
  return ref || null;
};

export const buildCatalogDatasetKey = (values = {}) => {
  const ref = catalogRefFromValue(values.catalogRef) || values.key;
  return buildCatalogDatasetKeyForRef(ref, values);
};

export const selectionRequiresProcessIds = (refs = [], catalog = []) =>
  refs.some((ref) => catalogRequiresProcessIds(findCatalogItem(catalog, ref), ref));

export const buildCatalogDatasetPayloads = (values = {}, catalog = []) => {
  const refs = catalogRefsFromValue(values.catalogRef);
  if (!refs.length && values.key) {
    const catalogItem = findCatalogItem(catalog, values.key);
    return [buildNativeDatasetPayload({ ...values, key: values.key }, catalogItem)];
  }
  return refs
    .map((ref) => {
      const catalogItem = findCatalogItem(catalog, ref);
      const key = buildCatalogDatasetKeyForRef(ref, values);
      if (!key) return null;
      let processIds = values.processIds;
      if (
        catalogRequiresProcessIds(catalogItem, key) &&
        !unwrapIds(processIds).length &&
        unwrapIds(values.templateProcessId)[0]
      ) {
        processIds = unwrapIds(values.templateProcessId);
      }
      return buildNativeDatasetPayload({ ...values, key, processIds }, catalogItem);
    })
    .filter((payload) => payload?.key);
};

export const datasetOmitsProcessIds = (key) => {
  const raw = String(key || '').trim();
  return GLOBAL_DATASET_KEYS.includes(raw) || isDynamicEntityKey(raw);
};

export const datasetRequiresProcessIds = (key) => {
  const raw = String(key || '').trim();
  if (isProcessFormDataKey(raw) || raw === CATALOG_KEY_PATTERN.ProcessFormData) return false;
  return PROCESS_RUNTIME_DATASET_KEYS.includes(raw);
};

export const catalogRequiresProcessIds = (catalogItem, key) => {
  if (catalogItem && catalogItem.requiresProcessIds != null) {
    return !!catalogItem.requiresProcessIds;
  }
  return datasetRequiresProcessIds(key);
};

export const catalogOmitsProcessIds = (catalogItem, key) => {
  if (catalogItem && catalogItem.requiresProcessIds === false) return true;
  return datasetOmitsProcessIds(key);
};

export const getResolvedCatalogItem = (key, catalog = [], resolvedByKey = {}) => {
  if (!key) return null;
  return resolvedByKey[key] || findCatalogItem(catalog, key);
};

export const getCatalogFields = (key, catalog = [], resolvedByKey = {}) => {
  const item = getResolvedCatalogItem(key, catalog, resolvedByKey);
  return Array.isArray(item?.fields) ? item.fields : [];
};

export const getCatalogRelation = (key, fieldKey, catalog = [], resolvedByKey = {}) => {
  const item = getResolvedCatalogItem(key, catalog, resolvedByKey);
  const relations = Array.isArray(item?.relations) ? item.relations : [];
  return relations.find((relation) => relation.sourceField === fieldKey) || null;
};

export const catalogDisplayName = (key, catalog = [], resolvedByKey = {}) => {
  const item = getResolvedCatalogItem(key, catalog, resolvedByKey);
  return item?.name || key;
};

export const collectDatasetResourceRefs = (items = []) => {
  const entities = new Set();
  const processes = new Set();
  const formsByProcess = {};
  items.forEach((item) => {
    const parsed = parseDatasetKey(item?.key);
    if (parsed.templateEntityId) entities.add(parsed.templateEntityId);
    if (parsed.templateProcessId) processes.add(parsed.templateProcessId);
    if (parsed.templateFormId && parsed.templateProcessId) {
      if (!formsByProcess[parsed.templateProcessId]) {
        formsByProcess[parsed.templateProcessId] = new Set();
      }
      formsByProcess[parsed.templateProcessId].add(parsed.templateFormId);
    }
    unwrapIds(item?.processIds).forEach((id) => processes.add(id));
  });
  return {
    entityIds: [...entities],
    processIds: [...processes],
    formsByProcess: Object.fromEntries(
      Object.entries(formsByProcess).map(([processId, ids]) => [processId, [...ids]]),
    ),
  };
};

export const datasetResourceLabel = (item, names = {}) => {
  const parsed = parseDatasetKey(item?.key);
  if (parsed.templateEntityId) return names[parsed.templateEntityId] || null;
  if (parsed.templateFormId) {
    const formName = names[parsed.templateFormId];
    const processName = names[parsed.templateProcessId];
    if (formName && processName && formName !== processName) {
      return `${processName} · ${formName}`;
    }
    return formName || processName || null;
  }
  return null;
};

export const datasetProcessLabels = (item, names = {}) =>
  unwrapIds(item?.processIds)
    .map((id) => names[id])
    .filter(Boolean);

export const buildNativeDatasetPayload = (values = {}, catalogItem) => {
  const key = values.key || buildCatalogDatasetKey(values);
  const payload = { key };
  if (catalogOmitsProcessIds(catalogItem, key) || isProcessFormDataKey(key)) return payload;
  const processIds = unwrapIds(values.processIds);
  if (processIds.length) payload.processIds = processIds;
  return payload;
};

export const serializeFilterValue = (value) => {
  if (value == null || value === '') return undefined;
  if (Array.isArray(value)) return value.join('; ');
  return String(value);
};

export const parseFilterValue = (raw, operator) => {
  if (operator === ReportFilterOperator.IsNull || operator === ReportFilterOperator.IsNotNull) {
    return undefined;
  }
  if (raw == null || raw === '') return undefined;
  if (Array.isArray(raw)) {
    if (operator === ReportFilterOperator.Between) return raw.slice(0, 2);
    if (operator === ReportFilterOperator.In || operator === ReportFilterOperator.NotIn) return raw;
    return raw[0];
  }
  const text = String(raw).trim();
  if (!text) return undefined;
  if (operator === ReportFilterOperator.In || operator === ReportFilterOperator.NotIn) {
    return text.split(/[;,]/).map((part) => part.trim()).filter(Boolean);
  }
  if (operator === ReportFilterOperator.Between) {
    const parts = text.split(/[;,]/).map((part) => part.trim()).filter(Boolean);
    return parts.length <= 1 ? parts[0] : parts.slice(0, 2);
  }
  return text;
};

export const buildNativeFilterPayload = (values = {}) => {
  const operator = normalizeFilterOperator(values.operator);
  const behavior = normalizeFilterBehavior(values.behavior);
  const payload = {
    key: values.key,
    label: values.label || undefined,
    datasetKey: values.datasetKey || undefined,
    fieldKey: values.fieldKey || undefined,
    behavior,
    operator,
  };
  if (behavior === ReportFilterBehavior.Context) {
    payload.value = FILTER_CONTEXT_VALUE;
  } else if (behavior === ReportFilterBehavior.Fixed) {
    const value = parseFilterValue(values.value, operator);
    if (value !== undefined) payload.value = value;
  }
  if (behavior === ReportFilterBehavior.Available) {
    const defaultValue = parseFilterValue(values.defaultValue, operator);
    if (defaultValue !== undefined) payload.defaultValue = defaultValue;
  }
  return payload;
};

export const toProcessSelectorValue = (processIds) => {
  const rows = Array.isArray(processIds) ? processIds : unwrapIds(processIds);
  return rows.filter(Boolean).map((row) => {
    if (typeof row === 'object') {
      const id = row.id ?? row.value;
      const name = row.name || row.displayName;
      return name && name !== id ? { id, name } : { id };
    }
    return { id: row };
  });
};

export const emptyDatasetDraft = () => ({
  catalogRef: null,
  templateProcessId: null,
  templateFormId: null,
  templateEntityId: null,
  processIds: [],
  manualKey: '',
});

export const draftFromDataset = (item) => {
  const parsed = parseDatasetKey(item?.key);
  return {
    catalogRef: parsed.catalogRef || null,
    templateProcessId: parsed.templateProcessId || null,
    templateFormId: parsed.templateFormId || null,
    templateEntityId: parsed.templateEntityId || null,
    processIds: toProcessSelectorValue(item?.processIds),
    manualKey: '',
  };
};

export const applyDraftCatalogRef = (draft, nextRef, catalog = []) => {
  const next = { ...draft, catalogRef: nextRef || null };
  if (!isProcessFormDataTemplate(next.catalogRef)) {
    next.templateProcessId = null;
    next.templateFormId = null;
  }
  if (!isDynamicEntityTemplate(next.catalogRef)) {
    next.templateEntityId = null;
  }
  if (
    next.catalogRef &&
    !catalogRequiresProcessIds(findCatalogItem(catalog, next.catalogRef), next.catalogRef)
  ) {
    next.processIds = [];
  }
  return next;
};

export const draftBuiltKey = (draft, hasCatalog) => {
  if (!hasCatalog) return String(draft.manualKey || '').trim() || null;
  return buildCatalogDatasetKeyForRef(draft.catalogRef, {
    templateProcessId: draft.templateProcessId,
    templateFormId: draft.templateFormId,
    templateEntityId: draft.templateEntityId,
  });
};

export const draftToPayload = (draft, catalog = [], hasCatalog = true) => {
  const key = draftBuiltKey(draft, hasCatalog);
  if (!key) return null;
  return buildNativeDatasetPayload(
    {
      key,
      processIds: draft.processIds,
      templateProcessId: draft.templateProcessId,
    },
    findCatalogItem(catalog, draft.catalogRef || key),
  );
};

export const draftNeedsProcessIds = (draft, catalog = []) =>
  selectionRequiresProcessIds(draft.catalogRef ? [draft.catalogRef] : [], catalog);

export const CATALOG_KIND_LABEL = Object.freeze({
  [ReportingDatasetKeyKind.Static]: 'reports.builder.catalog.kind.static',
  [ReportingDatasetKeyKind.ProcessFormData]: 'reports.builder.catalog.kind.processFormData',
  [ReportingDatasetKeyKind.DynamicEntity]: 'reports.builder.catalog.kind.dynamicEntity',
});

export const DATASET_FIELD_TYPE_LABEL = Object.freeze({
  [ReportingDatasetFieldType.Guid]: 'reports.builder.dataset.fieldType.guid',
  [ReportingDatasetFieldType.Text]: 'reports.builder.dataset.fieldType.text',
  [ReportingDatasetFieldType.Number]: 'reports.builder.dataset.fieldType.number',
  [ReportingDatasetFieldType.Boolean]: 'reports.builder.dataset.fieldType.boolean',
  [ReportingDatasetFieldType.Date]: 'reports.builder.dataset.fieldType.date',
  [ReportingDatasetFieldType.DateTime]: 'reports.builder.dataset.fieldType.datetime',
  [ReportingDatasetFieldType.User]: 'reports.builder.dataset.fieldType.user',
  [ReportingDatasetFieldType.Reference]: 'reports.builder.dataset.fieldType.reference',
  [ReportingDatasetFieldType.Position]: 'reports.builder.dataset.fieldType.position',
  [ReportingDatasetFieldType.Area]: 'reports.builder.dataset.fieldType.area',
  [ReportingDatasetFieldType.UserGroup]: 'reports.builder.dataset.fieldType.user-group',
});

const FIELD_TYPE_EQUALITY = Object.freeze([
  ReportFilterOperator.Equal,
  ReportFilterOperator.NotEqual,
  ReportFilterOperator.In,
  ReportFilterOperator.NotIn,
  ReportFilterOperator.IsNull,
  ReportFilterOperator.IsNotNull,
]);

const FIELD_TYPE_BOOLEAN = Object.freeze([
  ReportFilterOperator.Equal,
  ReportFilterOperator.NotEqual,
  ReportFilterOperator.IsNull,
  ReportFilterOperator.IsNotNull,
]);

const FIELD_TYPE_ORDERED = Object.freeze([
  ...FIELD_TYPE_EQUALITY,
  ReportFilterOperator.GreaterThan,
  ReportFilterOperator.GreaterThanOrEqual,
  ReportFilterOperator.LessThan,
  ReportFilterOperator.LessThanOrEqual,
  ReportFilterOperator.Between,
]);

const FIELD_TYPE_TEXT = Object.freeze([
  ...FIELD_TYPE_EQUALITY,
  ReportFilterOperator.Contains,
  ReportFilterOperator.StartsWith,
  ReportFilterOperator.EndsWith,
]);

const FIELD_TYPE_IDENTITY = new Set([
  ReportingDatasetFieldType.Guid,
  ReportingDatasetFieldType.User,
  ReportingDatasetFieldType.Position,
  ReportingDatasetFieldType.Area,
  ReportingDatasetFieldType.UserGroup,
  ReportingDatasetFieldType.Reference,
]);

export const operatorsForFieldType = (fieldType) => {
  if (!fieldType) return FILTER_OPERATORS;
  if (fieldType === ReportingDatasetFieldType.Text) return FIELD_TYPE_TEXT;
  if (
    fieldType === ReportingDatasetFieldType.Number ||
    fieldType === ReportingDatasetFieldType.Date ||
    fieldType === ReportingDatasetFieldType.DateTime
  ) {
    return FIELD_TYPE_ORDERED;
  }
  if (fieldType === ReportingDatasetFieldType.Boolean) return FIELD_TYPE_BOOLEAN;
  if (FIELD_TYPE_IDENTITY.has(fieldType)) return FIELD_TYPE_EQUALITY;
  return FILTER_OPERATORS;
};

export const coerceOperatorForFieldType = (operator, fieldType) => {
  const allowed = operatorsForFieldType(fieldType);
  return allowed.includes(operator) ? operator : ReportFilterOperator.Equal;
};

export const FILTER_BEHAVIOR_LABEL = Object.freeze({
  [ReportFilterBehavior.Fixed]: 'reports.builder.filter.behavior.fixed',
  [ReportFilterBehavior.Available]: 'reports.builder.filter.behavior.available',
  [ReportFilterBehavior.Context]: 'reports.builder.filter.behavior.context',
});

export const FILTER_OPERATOR_LABEL = Object.freeze({
  [ReportFilterOperator.Equal]: 'reports.builder.filter.operator.eq',
  [ReportFilterOperator.NotEqual]: 'reports.builder.filter.operator.neq',
  [ReportFilterOperator.In]: 'reports.builder.filter.operator.in',
  [ReportFilterOperator.NotIn]: 'reports.builder.filter.operator.notIn',
  [ReportFilterOperator.GreaterThan]: 'reports.builder.filter.operator.gt',
  [ReportFilterOperator.GreaterThanOrEqual]: 'reports.builder.filter.operator.gte',
  [ReportFilterOperator.LessThan]: 'reports.builder.filter.operator.lt',
  [ReportFilterOperator.LessThanOrEqual]: 'reports.builder.filter.operator.lte',
  [ReportFilterOperator.Contains]: 'reports.builder.filter.operator.contains',
  [ReportFilterOperator.StartsWith]: 'reports.builder.filter.operator.startsWith',
  [ReportFilterOperator.EndsWith]: 'reports.builder.filter.operator.endsWith',
  [ReportFilterOperator.Between]: 'reports.builder.filter.operator.between',
  [ReportFilterOperator.IsNull]: 'reports.builder.filter.operator.isNull',
  [ReportFilterOperator.IsNotNull]: 'reports.builder.filter.operator.isNotNull',
});

export const operatorNeedsValue = (operator) =>
  operator !== ReportFilterOperator.IsNull && operator !== ReportFilterOperator.IsNotNull;

export const filterBehaviorNeedsValue = (behavior) =>
  behavior === ReportFilterBehavior.Fixed || behavior === ReportFilterBehavior.Context;

export const filterBehaviorNeedsDefaultValue = (behavior) =>
  behavior === ReportFilterBehavior.Available;

export const emptyFilterDraft = () => ({
  key: '',
  label: '',
  datasetKey: null,
  fieldKey: null,
  behavior: ReportFilterBehavior.Available,
  operator: ReportFilterOperator.Equal,
  value: '',
  defaultValue: '',
});

export const applyFilterBehaviorChange = (draft, rawBehavior) => {
  const behavior = normalizeFilterBehavior(rawBehavior, draft?.behavior);
  if (!draft || behavior === draft.behavior) return draft;
  const next = { ...draft, behavior };
  if (filterBehaviorNeedsDefaultValue(behavior)) {
    next.value = '';
  } else {
    next.defaultValue = '';
    if (behavior === ReportFilterBehavior.Context) next.value = FILTER_CONTEXT_VALUE;
  }
  return next;
};

export const applyFilterDraftPatch = (draft, patchOrFn) => {
  const current = draft || emptyFilterDraft();
  const patch = typeof patchOrFn === 'function' ? patchOrFn(current) : patchOrFn;
  if (!patch || patch === current) return current;
  return { ...current, ...patch };
};

export const draftFromFilter = (item) => ({
  key: item?.key || '',
  label: item?.label || '',
  datasetKey: item?.datasetKey || null,
  fieldKey: item?.fieldKey || null,
  behavior: normalizeFilterBehavior(item?.behavior),
  operator: normalizeFilterOperator(item?.operator),
  value: serializeFilterValue(item?.value) || '',
  defaultValue: serializeFilterValue(item?.defaultValue) || '',
});

export const draftToFilterPayload = (draft) =>
  buildNativeFilterPayload({
    ...draft,
    datasetKey: draft.datasetKey || undefined,
    fieldKey: draft.fieldKey || undefined,
  });

export const QUERY_TYPES = Object.values(ReportingQueryType);
export const QUERY_METRIC_OPERATIONS = Object.values(ReportingQueryMetricOperation);
export const QUERY_DATE_BUCKETS = Object.values(ReportingQueryDateBucket);
export const QUERY_ORDER_DIRECTIONS = Object.values(ReportingQueryOrderDirection);
export const QUERY_MAX_LIMIT = 5000;
export const QUERY_DEFAULT_ROWS_LIMIT = 1000;
export const QUERY_SEMANTIC_KEY = /^[A-Za-z_][A-Za-z0-9_]*$/;

export const emptyParameterDraft = () => ({ key: '' });

export const draftFromParameter = (item) => ({
  key: item?.key || '',
});

export const draftToParameterPayload = (draft) => ({
  key: String(draft?.key || '').trim(),
});

export const validateParameterDraft = (
  draft,
  { parameters = [], editingKey } = {},
) => {
  const key = String(draft?.key || '').trim();
  if (!key || !QUERY_SEMANTIC_KEY.test(key)) {
    return { errorId: 'reports.builder.parameter.keyInvalid' };
  }
  if (parameters.some((item) => item.key === key && item.key !== editingKey)) {
    return { errorId: 'reports.builder.parameter.duplicate' };
  }
  return { ok: true };
};

export const QUERY_TYPE_LABEL = Object.freeze({
  [ReportingQueryType.Rows]: 'reports.builder.query.type.rows',
  [ReportingQueryType.Aggregate]: 'reports.builder.query.type.aggregate',
});

export const QUERY_METRIC_LABEL = Object.freeze({
  [ReportingQueryMetricOperation.Count]: 'reports.builder.query.metric.count',
  [ReportingQueryMetricOperation.CountDistinct]: 'reports.builder.query.metric.countDistinct',
  [ReportingQueryMetricOperation.Sum]: 'reports.builder.query.metric.sum',
  [ReportingQueryMetricOperation.Average]: 'reports.builder.query.metric.average',
  [ReportingQueryMetricOperation.Min]: 'reports.builder.query.metric.min',
  [ReportingQueryMetricOperation.Max]: 'reports.builder.query.metric.max',
});

export const QUERY_BUCKET_LABEL = Object.freeze({
  [ReportingQueryDateBucket.Day]: 'reports.builder.query.bucket.day',
  [ReportingQueryDateBucket.Week]: 'reports.builder.query.bucket.week',
  [ReportingQueryDateBucket.Month]: 'reports.builder.query.bucket.month',
  [ReportingQueryDateBucket.Quarter]: 'reports.builder.query.bucket.quarter',
  [ReportingQueryDateBucket.Year]: 'reports.builder.query.bucket.year',
});

export const QUERY_PARAMETER_TYPES = Object.freeze([
  ReportingDatasetFieldType.Guid,
  ReportingDatasetFieldType.Text,
  ReportingDatasetFieldType.Number,
  ReportingDatasetFieldType.Boolean,
  ReportingDatasetFieldType.Date,
  ReportingDatasetFieldType.DateTime,
]);

export const metricNeedsField = (operation) =>
  operation !== ReportingQueryMetricOperation.Count;

export const metricAllowsFieldType = (operation, fieldType) => {
  if (!operation) return false;
  if (operation === ReportingQueryMetricOperation.Count) return true;
  if (operation === ReportingQueryMetricOperation.CountDistinct) return !!fieldType;
  if (
    operation === ReportingQueryMetricOperation.Sum ||
    operation === ReportingQueryMetricOperation.Average
  ) {
    return fieldType === ReportingDatasetFieldType.Number;
  }
  if (
    operation === ReportingQueryMetricOperation.Min ||
    operation === ReportingQueryMetricOperation.Max
  ) {
    return [
      ReportingDatasetFieldType.Number,
      ReportingDatasetFieldType.Date,
      ReportingDatasetFieldType.DateTime,
      ReportingDatasetFieldType.Text,
    ].includes(fieldType);
  }
  return false;
};

export const fieldAllowsBucket = (fieldType) =>
  fieldType === ReportingDatasetFieldType.Date ||
  fieldType === ReportingDatasetFieldType.DateTime;

export const queriesUsingDataset = (queries = [], datasetKey) =>
  (queries || []).filter((query) => query?.datasetKey === datasetKey);

export const emptyQueryDraft = () => ({
  key: '',
  datasetKey: null,
  type: ReportingQueryType.Rows,
  select: [],
  dimensions: [],
  metrics: [],
  filterField: null,
  filterOperator: ReportFilterOperator.Equal,
  filterValue: '',
  filterParameterKey: '',
  parameters: [],
  orderField: '',
  orderDirection: ReportingQueryOrderDirection.Descending,
  limit: '',
});

const unwrapSelectKeys = (raw) => {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((row) => {
      if (typeof row === 'string') return row;
      return row?.value ?? row?.key ?? row?.id ?? null;
    })
    .filter(Boolean);
};

export const draftFromQuery = (item) => {
  const filter = item?.filter;
  const comparison = filter?.field ? filter : Array.isArray(filter?.items) ? filter.items[0] : null;
  const order = Array.isArray(item?.orderBy) ? item.orderBy[0] : null;
  return {
    key: item?.key || '',
    datasetKey: item?.datasetKey || null,
    type: item?.type === ReportingQueryType.Aggregate ? ReportingQueryType.Aggregate : ReportingQueryType.Rows,
    select: Array.isArray(item?.select) ? item.select : [],
    dimensions: Array.isArray(item?.dimensions)
      ? item.dimensions.map((row) => ({
          key: row.key || row.field || '',
          field: row.field || '',
          bucket: row.bucket || undefined,
        }))
      : [],
    metrics: Array.isArray(item?.metrics)
      ? item.metrics.map((row) => ({
          key: row.key || '',
          operation: row.operation || ReportingQueryMetricOperation.Count,
          field: row.field || '',
        }))
      : [],
    filterField: comparison?.field || null,
    filterOperator: comparison?.operator || ReportFilterOperator.Equal,
    filterValue:
      comparison?.value != null
        ? String(comparison.value)
        : Array.isArray(comparison?.values)
        ? comparison.values.join('; ')
        : '',
    filterParameterKey: comparison?.parameterKey || '',
    parameters: Array.isArray(item?.parameters)
      ? item.parameters.map((row) => ({
          key: row.key || '',
          type: row.type || ReportingDatasetFieldType.Text,
          required: !!row.required,
        }))
      : [],
    orderField: order?.field || '',
    orderDirection: order?.direction || ReportingQueryOrderDirection.Descending,
    limit: item?.limit != null ? String(item.limit) : '',
  };
};

const buildQueryFilter = (draft) => {
  const field = draft.filterField;
  if (!field) return undefined;
  const operator = normalizeFilterOperator(draft.filterOperator);
  const parameterKey = String(draft.filterParameterKey || '').trim();
  const node = { field, operator };
  if (parameterKey) {
    node.parameterKey = parameterKey;
    return node;
  }
  if (!operatorNeedsValue(operator)) return node;
  const value = parseFilterValue(draft.filterValue, operator);
  if (value === undefined) return undefined;
  if (Array.isArray(value)) node.values = value;
  else node.value = value;
  return node;
};

export const validateQueryDraft = (draft, { datasets = [], queries = [], catalog = [], resolvedByKey = {}, editingKey } = {}) => {
  const key = String(draft.key || '').trim();
  if (!key || !QUERY_SEMANTIC_KEY.test(key)) {
    return { errorId: 'reports.builder.query.keyInvalid' };
  }
  if (!draft.datasetKey) return { errorId: 'form.error.required' };
  if (!datasets.some((item) => item.key === draft.datasetKey)) {
    return { errorId: 'reports.builder.query.datasetMissing' };
  }
  if (queries.some((item) => item.key === key && item.key !== editingKey)) {
    return { errorId: 'reports.builder.query.duplicate' };
  }

  const fields = getCatalogFields(draft.datasetKey, catalog, resolvedByKey);
  const fieldByKey = fields.reduce((acc, field) => {
    acc[field.key] = field;
    return acc;
  }, {});
  const type = draft.type === ReportingQueryType.Aggregate ? ReportingQueryType.Aggregate : ReportingQueryType.Rows;

  if (type === ReportingQueryType.Rows) {
    const select = unwrapSelectKeys(draft.select);
    if (!select.length) return { errorId: 'reports.builder.query.selectRequired' };
    if (select.some((fieldKey) => fields.length && !fieldByKey[fieldKey])) {
      return { errorId: 'reports.builder.query.unknownField' };
    }
    if (draft.orderField && !select.includes(draft.orderField)) {
      return { errorId: 'reports.builder.query.orderFieldInvalid' };
    }
  } else {
    const metrics = (draft.metrics || []).filter((row) => row?.key && row?.operation);
    if (!metrics.length) return { errorId: 'reports.builder.query.metricsRequired' };
    const outputKeys = new Set();
    for (const metric of metrics) {
      if (!QUERY_SEMANTIC_KEY.test(metric.key)) return { errorId: 'reports.builder.query.keyInvalid' };
      if (outputKeys.has(metric.key)) return { errorId: 'reports.builder.query.outputKeyDuplicate' };
      outputKeys.add(metric.key);
      if (metricNeedsField(metric.operation) && !metric.field) {
        return { errorId: 'reports.builder.query.metricFieldRequired' };
      }
      if (metric.field && fields.length && !fieldByKey[metric.field]) {
        return { errorId: 'reports.builder.query.unknownField' };
      }
      if (metric.field && !metricAllowsFieldType(metric.operation, fieldByKey[metric.field]?.type)) {
        return { errorId: 'reports.builder.query.metricTypeInvalid' };
      }
    }
    for (const dimension of draft.dimensions || []) {
      if (!dimension?.field) continue;
      if (!dimension.key || !QUERY_SEMANTIC_KEY.test(dimension.key)) {
        return { errorId: 'reports.builder.query.keyInvalid' };
      }
      if (outputKeys.has(dimension.key)) return { errorId: 'reports.builder.query.outputKeyDuplicate' };
      outputKeys.add(dimension.key);
      if (fields.length && !fieldByKey[dimension.field]) {
        return { errorId: 'reports.builder.query.unknownField' };
      }
      if (dimension.bucket && !fieldAllowsBucket(fieldByKey[dimension.field]?.type)) {
        return { errorId: 'reports.builder.query.bucketInvalid' };
      }
    }
    if (draft.orderField && !outputKeys.has(draft.orderField)) {
      return { errorId: 'reports.builder.query.orderFieldInvalid' };
    }
  }

  if (draft.limit !== '' && draft.limit != null) {
    const limit = Number(draft.limit);
    if (!Number.isFinite(limit) || limit <= 0 || limit > QUERY_MAX_LIMIT) {
      return { errorId: 'reports.builder.query.limitInvalid' };
    }
  }

  const parameterKeys = new Set();
  for (const parameter of draft.parameters || []) {
    const parameterKey = String(parameter.key || '').trim();
    if (!parameterKey) continue;
    if (!QUERY_SEMANTIC_KEY.test(parameterKey) || !QUERY_PARAMETER_TYPES.includes(parameter.type)) {
      return { errorId: 'reports.builder.query.parameterInvalid' };
    }
    if (parameterKeys.has(parameterKey)) return { errorId: 'reports.builder.query.parameterDuplicate' };
    parameterKeys.add(parameterKey);
  }

  if (draft.filterParameterKey && !parameterKeys.has(String(draft.filterParameterKey).trim())) {
    return { errorId: 'reports.builder.query.filterParameterMissing' };
  }

  return { ok: true };
};

export const draftToQueryPayload = (draft) => {
  const type = draft.type === ReportingQueryType.Aggregate ? ReportingQueryType.Aggregate : ReportingQueryType.Rows;
  const payload = {
    key: String(draft.key || '').trim(),
    datasetKey: draft.datasetKey,
    type,
  };

  if (type === ReportingQueryType.Rows) {
    payload.select = unwrapSelectKeys(draft.select);
  } else {
    payload.metrics = (draft.metrics || [])
      .filter((row) => row?.key && row?.operation)
      .map((row) => {
        const metric = { key: row.key, operation: row.operation };
        if (row.field && metricNeedsField(row.operation)) metric.field = row.field;
        return metric;
      });
    const dimensions = (draft.dimensions || [])
      .filter((row) => row?.key && row?.field)
      .map((row) => {
        const dimension = { key: row.key, field: row.field };
        if (row.bucket) dimension.bucket = row.bucket;
        return dimension;
      });
    if (dimensions.length) payload.dimensions = dimensions;
  }

  const filter = buildQueryFilter(draft);
  if (filter) payload.filter = filter;

  const parameters = (draft.parameters || [])
    .map((row) => ({
      key: String(row.key || '').trim(),
      type: row.type,
      ...(row.required ? { required: true } : {}),
    }))
    .filter((row) => row.key);
  if (parameters.length) payload.parameters = parameters;

  if (draft.orderField) {
    payload.orderBy = [
      {
        field: draft.orderField,
        direction: draft.orderDirection || ReportingQueryOrderDirection.Descending,
      },
    ];
  }

  if (draft.limit !== '' && draft.limit != null) {
    payload.limit = Number(draft.limit);
  }

  return payload;
};
